import { useState } from 'react';
import { Building2, ClipboardList, Landmark, LogOut, Menu, Package, User, Wrench, X } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { LiveClock } from './figma-ui';
import { changeLanguage, getCurrentLanguage, type SupportedLanguage } from '../../i18n';
import { useTranslation } from 'react-i18next';
import { formatRelativeClosureDate } from '../screens/cloture/cloture-helpers';
import { resolveFrontofficeRuntime } from '../../runtime/frontofficeRuntime';
import { posMenuItems, type PosMenuItemId } from './posMenuItems';

export interface LastClosureInfo {
  confirmedAt: string;
  difference: number;
  status: string;
}

export interface ActiveStockCountInfo {
  visibleId: string;
  percent: number;
}

/** Figma Make POSTopBar — compressed below xl for 1024×768 POS monitors. */
interface PosTopBarProps {
  cashierName: string;
  branchName?: string;
  userRole?: string;
  mode?: 'sale' | 'refund';
  lastClosure?: LastClosureInfo | null;
  activeStockCount?: ActiveStockCountInfo | null;
  isOnline?: boolean;
  pendingSyncCount?: number;
  onLogout: () => void;
  onResetTerminal?: () => void;
  onQuitApp?: () => void;
  onEditProfile?: () => void;
  onInventory: () => void;
  onStockCount?: () => void;
  onCloture?: () => void;
  /** Return error message to block (e.g. offline); null to allow */
  guardOnlineAction?: (feature: 'inventaire' | 'cloture' | 'profile') => string | null;
  onOnlineRequiredMessage?: (message: string) => void;
}

const LANGS: { code: SupportedLanguage; label: string }[] = [
  { code: 'fr', label: 'FR' },
  { code: 'en', label: 'EN' },
  { code: 'ar', label: 'AR' },
];

function menuIcon(id: PosMenuItemId) {
  const size = 13;
  if (id === 'stock') return <Package size={size} />;
  if (id === 'cloture') return <Landmark size={size} />;
  if (id === 'inventaire') return <ClipboardList size={size} />;
  if (id === 'reset') return <Wrench size={size} />;
  if (id === 'quit') return <X size={size} />;
  if (id === 'profil') return <User size={size} />;
  return <LogOut size={size} />;
}

function menuTone(id: PosMenuItemId): string {
  if (id === 'cloture') return 'text-[#0D7377]';
  if (id === 'inventaire') return 'text-amber-400';
  if (id === 'reset') return 'text-orange-400';
  if (id === 'quit' || id === 'logout') return 'text-red-400';
  return 'text-gray-200';
}

export function PosTopBar({
  cashierName,
  branchName,
  userRole = 'cashier',
  mode = 'sale',
  lastClosure,
  activeStockCount,
  isOnline = true,
  pendingSyncCount = 0,
  onLogout,
  onResetTerminal,
  onQuitApp,
  onEditProfile,
  onInventory,
  onStockCount,
  onCloture,
  guardOnlineAction,
  onOnlineRequiredMessage,
}: PosTopBarProps) {
  const { t } = useTranslation();
  const currentLang = getCurrentLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const inventaireLabel = t('pos.menu.inventaire');
  const items = posMenuItems({
    role: userRole,
    runtime: resolveFrontofficeRuntime(),
  });
  const canCloture = items.includes('cloture');

  const runGuarded = (
    feature: 'inventaire' | 'cloture' | 'profile',
    action?: () => void,
  ) => {
    if (!action) return;
    const blocked = guardOnlineAction?.(feature) ?? null;
    if (blocked) {
      onOnlineRequiredMessage?.(blocked);
      return;
    }
    action();
  };

  const chooseMenuItem = (id: PosMenuItemId) => {
    if (id === 'reset') {
      setConfirmReset(true);
      return;
    }
    setMenuOpen(false);
    if (id === 'stock') onInventory();
    if (id === 'cloture') runGuarded('cloture', onCloture);
    if (id === 'inventaire') runGuarded('inventaire', onStockCount);
    if (id === 'quit') onQuitApp?.();
    if (id === 'profil') runGuarded('profile', onEditProfile);
    if (id === 'logout') onLogout();
  };

  const dotColor =
    !lastClosure
      ? 'bg-gray-500'
      : lastClosure.status === 'balanced'
        ? 'bg-emerald-500'
        : lastClosure.difference > 0
          ? 'bg-brand'
          : 'bg-red-500';

  return (
    <div
      className={`h-12 xl:h-14 flex items-center px-2.5 xl:px-5 gap-1.5 xl:gap-3 border-b shrink-0 min-w-0 ${
        mode === 'refund' ? 'bg-amber-950 border-amber-900' : 'bg-[#0C1821] border-gray-800'
      }`}
    >
      <BrandLogo size="xs" inverted />
      <div className="hidden sm:block w-px h-5 bg-gray-700 shrink-0" />
      <div
        className="hidden sm:flex items-center gap-1 text-gray-300 text-xs xl:text-sm min-w-0 max-w-[88px] xl:max-w-[160px]"
        title={branchName || t('cashier.store_name')}
      >
        <Building2 size={12} className="text-gray-600 shrink-0" />
        <span className="truncate">{branchName || t('cashier.store_name')}</span>
      </div>
      <div className="hidden md:block w-px h-5 bg-gray-700 shrink-0" />
      <div
        className="hidden md:flex items-center gap-1 text-gray-300 text-xs xl:text-sm min-w-0 max-w-[72px] xl:max-w-[140px]"
        title={cashierName}
      >
        <User size={12} className="text-gray-600 shrink-0" />
        <span className="truncate">{cashierName}</span>
      </div>
      {mode === 'refund' && (
        <span className="ml-0.5 px-1.5 py-0.5 rounded text-[10px] xl:text-xs font-medium bg-brand-accent/15 text-amber-400 border border-amber-800/40 shrink-0">
          <span className="xl:hidden">REMB.</span>
          <span className="hidden xl:inline">Mode Remboursement</span>
        </span>
      )}
      {activeStockCount && (
        <span
          className="ml-0.5 px-1.5 py-0.5 rounded text-[10px] xl:text-xs font-medium bg-brand/15 text-brand border border-brand/30 tabular-nums truncate max-w-[100px] xl:max-w-none shrink-0"
          title={t('pos.stockcount.active_banner', {
            id: activeStockCount.visibleId,
            percent: Math.round(activeStockCount.percent),
          })}
        >
          {t('pos.stockcount.active_banner', {
            id: activeStockCount.visibleId,
            percent: Math.round(activeStockCount.percent),
          })}
        </span>
      )}
      <div className="flex-1 min-w-0" />
      {!isOnline && (
        <span
          className="mr-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-950 text-red-300 border border-red-800/50 shrink-0"
          title="Mode hors ligne"
        >
          Hors ligne{pendingSyncCount > 0 ? ` · ${pendingSyncCount}` : ''}
        </span>
      )}
      {isOnline && pendingSyncCount > 0 && (
        <span className="mr-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-950 text-amber-300 border border-amber-800/40 shrink-0">
          Sync {pendingSyncCount}
        </span>
      )}
      {canCloture && (
        <div
          className="hidden xl:flex items-center gap-2 text-xs text-gray-500 mr-2 shrink-0"
          title={lastClosure ? formatRelativeClosureDate(lastClosure.confirmedAt) : 'Aucune clôture effectuée'}
        >
          <span className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
          <span className="font-medium text-gray-400">Clôture</span>
          <span className="text-gray-300">
            {lastClosure ? formatRelativeClosureDate(lastClosure.confirmedAt) : 'Aucune clôture effectuée'}
          </span>
        </div>
      )}
      {canCloture && (
        <span
          className={`xl:hidden w-2 h-2 rounded-full shrink-0 mr-1 ${dotColor}`}
          title={lastClosure ? formatRelativeClosureDate(lastClosure.confirmedAt) : 'Aucune clôture effectuée'}
        />
      )}
      {canCloture && <div className="hidden xl:block w-px h-5 bg-gray-700 shrink-0" />}
      <LiveClock className="text-gray-300 shrink-0" showSeconds={false} />
      <div className="flex items-center gap-0.5 bg-gray-800 rounded-md p-0.5 mx-0.5 xl:mx-2 shrink-0">
        {LANGS.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            onClick={() => changeLanguage(code)}
            className={`px-1.5 xl:px-2 py-1 rounded text-[10px] xl:text-xs font-medium transition-colors ${
              currentLang === code ? 'bg-gray-600 text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="relative shrink-0">
        <button
          type="button"
          data-testid="pos-menu-button"
          aria-label={t('pos.menu.open')}
          aria-expanded={menuOpen}
          onClick={() => {
            setConfirmReset(false);
            setMenuOpen((open) => !open);
          }}
          className="w-8 h-8 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 flex items-center justify-center"
        >
          <Menu size={15} />
        </button>
        {menuOpen && (
          <>
            <button
              type="button"
              aria-label={t('pos.menu.open')}
              className="fixed inset-0 z-40 cursor-default"
              onClick={() => {
                setMenuOpen(false);
                setConfirmReset(false);
              }}
            />
            <div className="absolute right-0 top-full mt-2 z-50 w-56 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl py-1">
              {confirmReset ? (
                <div className="px-4 py-3 space-y-2">
                  <p className="text-sm text-white">{t('login.reset_title')}</p>
                  <p className="text-xs text-gray-400">{t('login.reset_body')}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-xs text-gray-400"
                      onClick={() => setConfirmReset(false)}
                    >
                      {t('login.reset_cancel')}
                    </button>
                    <button
                      type="button"
                      data-testid="pos-menu-reset-confirm"
                      className="text-xs text-red-300"
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirmReset(false);
                        onResetTerminal?.();
                      }}
                    >
                      {t('login.reset_confirm')}
                    </button>
                  </div>
                </div>
              ) : (
                items.map((id) => (
                  <div key={id}>
                    {(id === 'reset' || id === 'profil') && (
                      <div className="my-1 border-t border-gray-800" />
                    )}
                    <button
                      type="button"
                      data-testid={`pos-menu-${id}`}
                      onClick={() => chooseMenuItem(id)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-medium hover:bg-gray-800 ${menuTone(id)}`}
                    >
                      {menuIcon(id)}
                      {id === 'inventaire' ? inventaireLabel : t(`pos.menu.${id}`)}
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export type PosScreen =
  | 'selling'
  | 'checkout'
  | 'success'
  | 'refund'
  | 'inventory'
  | 'history'
  | 'cloture'
  | 'stockcount';
